// Page-at-a-time rendering for the big admin tables. Thousands of rows — each
// with a photo, editable cells and inputs — in one <table> make every keystroke
// and checkbox click re-render all of them; one page keeps the DOM small.
import React, { useEffect, useState } from 'react';

export const PAGE_SIZE = 100;

// Current page for a list of `total` rows. Back to the first page whenever
// `resetKey` changes (a new filter or a fresh load); clamped when rows go away.
export function usePage(total: number, resetKey: unknown) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [resetKey]);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const p = Math.min(page, pages - 1);
  return { page: p, pages, setPage, start: p * PAGE_SIZE, end: (p + 1) * PAGE_SIZE };
}

export const Pager: React.FC<{
  page: number;
  pages: number;
  total: number;
  setPage: (p: number) => void;
}> = ({ page, pages, total, setPage }) => {
  if (pages <= 1) return null;
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  return (
    <div className="pager">
      <button className="sm" disabled={page === 0} onClick={() => setPage(0)} title="First page">
        «
      </button>
      <button className="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
        ‹ Prev
      </button>
      <span className="count">
        <b>
          {from}–{to}
        </b>{' '}
        of {total} · page{' '}
        <select value={page} onChange={(e) => setPage(Number(e.target.value))}>
          {Array.from({ length: pages }, (_, i) => (
            <option key={i} value={i}>
              {i + 1}
            </option>
          ))}
        </select>{' '}
        / {pages}
      </span>
      <button className="sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>
        Next ›
      </button>
      <button className="sm" disabled={page >= pages - 1} onClick={() => setPage(pages - 1)} title="Last page">
        »
      </button>
    </div>
  );
};
